import React, { useEffect, useState } from 'react';
import { createBranch, updateBranch, getBranches } from '../services/branchService';
import DepositManager from './DepositManager';

const EMPTY_FORM = { name: '', address: '', phone: '', email: '', city: '' };

export default function BranchForm({ branch, onCancel, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [currentBranch, setCurrentBranch] = useState(branch);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const isEditing = Boolean(branch);

  useEffect(() => {
    if (branch) {
      setForm({
        name: branch.name || '',
        address: branch.address || '',
        phone: branch.phone || '',
        email: branch.email || '',
        city: branch.city || '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setCurrentBranch(branch);
    setError('');
  }, [branch]);

  function handleChange(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSaving(true);

    const payload = {
      name: form.name.trim(),
      address: form.address.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      city: form.city.trim(),
    };

    try {
      if (currentBranch) {
        const updated = await updateBranch(currentBranch.id, payload);
        setCurrentBranch(b => ({ ...b, ...updated, ...payload }));
      } else {
        const res = await createBranch(payload);
        const created = res.branch ?? res;
        setCurrentBranch({ ...created, deposits: created.deposits || [] });
      }
    } catch (err) {
      const msg = err.response?.data?.msg || 'No se pudo guardar la sucursal.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  async function refreshDeposits() {
    try {
      const branches = await getBranches();
      const updated = branches.find(b => b.id === currentBranch.id);
      if (updated) setCurrentBranch(updated);
    } catch {
      // ignore
    }
  }

  return (
    <div className="section-card">
      <div className="section-header">
        <h6>
          <i className={`bi ${isEditing ? 'bi-pencil-square' : 'bi-plus-circle'}`} style={{ color: 'var(--orig-green)' }} />
          {isEditing ? 'Editar Sucursal' : 'Nueva Sucursal'}
        </h6>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="section-body">
          {error && (
            <div className="alert-orig alert-error" style={{ marginBottom: 16 }}>
              <i className="bi bi-exclamation-triangle-fill" />
              {error}
            </div>
          )}

          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="form-label">Nombre</label>
              <input
                type="text"
                className="form-control"
                placeholder="Ej: Sucursal Guanare"
                value={form.name}
                onChange={e => handleChange('name', e.target.value)}
                required
              />
            </div>

            <div className="col-12 col-md-6">
              <label className="form-label">Dirección</label>
              <input
                type="text"
                className="form-control"
                placeholder="Ej: Guanare Av. Principal"
                value={form.address}
                onChange={e => handleChange('address', e.target.value)}
                required
              />
            </div>

            <div className="col-6 col-md-4">
              <label className="form-label">Teléfono <span style={{ opacity: 0.5, fontSize: '0.8em' }}>(opcional)</span></label>
              <input
                type="text"
                className="form-control"
                placeholder="Ej: 0414-567.01.02"
                value={form.phone}
                onChange={e => handleChange('phone', e.target.value)}
              />
            </div>

            <div className="col-6 col-md-4">
              <label className="form-label">Correo <span style={{ opacity: 0.5, fontSize: '0.8em' }}>(opcional)</span></label>
              <input
                type="email"
                className="form-control"
                placeholder="correo@ejemplo.com"
                value={form.email}
                onChange={e => handleChange('email', e.target.value)}
              />
            </div>

            <div className="col-12 col-md-4">
              <label className="form-label">Ciudad</label>
              <input
                type="text"
                className="form-control"
                placeholder="Ej: Guanare"
                value={form.city}
                onChange={e => handleChange('city', e.target.value)}
                required
              />
            </div>
          </div>
        </div>

        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 10,
          padding: '14px 20px', borderTop: '1px solid var(--orig-border)',
        }}>
          <button type="button" className="btn-orig btn-orig-outline" onClick={onCancel}>
            Cancelar
          </button>
          <button type="submit" className="btn-orig btn-orig-green" disabled={saving}>
            <i className="bi bi-check-lg" />
            {saving ? 'Guardando...' : currentBranch ? 'Guardar cambios' : 'Crear Sucursal'}
          </button>
          {currentBranch && (
            <button type="button" className="btn-orig btn-orig-green" onClick={onSaved}>
              <i className="bi bi-check2-circle" />
              Finalizar
            </button>
          )}
        </div>
      </form>

      {currentBranch && (
        <div style={{ borderTop: '1px solid var(--orig-border)' }}>
          <div className="section-header">
            <h6>
              <i className="bi bi-building" style={{ color: 'var(--orig-green)' }} />
              Depósitos de la sucursal
            </h6>
          </div>
          <div className="section-body">
            <DepositManager
              branchId={currentBranch.id}
              deposits={currentBranch.deposits || []}
              onChange={refreshDeposits}
            />
          </div>
        </div>
      )}
    </div>
  );
}
